import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Activity, RefreshCw, Bell, Zap, CheckCircle, Clock, RotateCcw, Pause, Play, Trash2, Eye } from "lucide-react";
import Header from "../components/layout/Header";
import { SearchInput } from "../components/ui/index";
import { evenementsAPI, cascadeAPI, projetsAPI } from "../api/services";
import { useAuth } from "../context/AuthContext";
import { buildEvenementPreview } from "../utils/naturalLanguage";

const statsBadge = {
  "Actif": "bg-green-100 text-green-700 border border-green-200",
  "En pause": "bg-yellow-100 text-yellow-700 border border-yellow-200",
};

const typeBadge = "bg-gray-100 text-slate-600 border border-gray-200";
const clientBadge = "bg-blue-50 text-blue-700 border border-blue-200";
const projetBadge = "bg-purple-50 text-purple-700 border border-purple-200";

export default function Evenements() {
  const { t } = useTranslation();
  const { user, canDo, adminMode } = useAuth();
  const isMembre = user?.role === 'membre';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [filterRevendeur, setFilterRevendeur] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterProjet, setFilterProjet] = useState(searchParams.get("projet") || "");

  // Data state
  const [evenements, setEvenements] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cascade filter state
  const [revendeurs, setRevendeurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [projets, setProjets] = useState([]);

  const filtersSelected = !!filterProjet;

  // Fetch evenements
  const fetchEvenements = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filterRevendeur) params.revendeur = filterRevendeur;
      if (filterClient) params.client = filterClient;
      if (filterProjet) params.projet = filterProjet;

      const { data } = await evenementsAPI.getAll(params);
      const list = data.data || data;
      setEvenements(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Erreur chargement événements:", err);
    } finally {
      setLoading(false);
    }
  }, [search, filterRevendeur, filterClient, filterProjet]);

  useEffect(() => { fetchEvenements(); }, [fetchEvenements]);

  // Membre : charger ses projets IoT (directs + via chantiers)
  useEffect(() => {
    if (!isMembre) return;
    projetsAPI.mesProjetsIoT().then(res => {
      setProjets(res.data.data || res.data || []);
    }).catch(console.error);
  }, [isMembre]);

  // Admin/Revendeur : cascade classique
  useEffect(() => {
    if (isMembre) return;
    cascadeAPI.revendeurs().then(res => {
      setRevendeurs(res.data.data || res.data || []);
    }).catch(console.error);
  }, [isMembre]);

  useEffect(() => {
    if (isMembre) return;
    cascadeAPI.clients(filterRevendeur || undefined).then(res => {
      setClients(res.data.data || res.data || []);
    }).catch(console.error);
  }, [filterRevendeur, isMembre]);

  useEffect(() => {
    if (isMembre) return;
    cascadeAPI.projets(filterClient || undefined, filterRevendeur || undefined).then(res => {
      setProjets(res.data.data || res.data || []);
    }).catch(console.error);
  }, [filterClient, filterRevendeur, isMembre]);

  const handleRevendeurChange = (v) => { setFilterRevendeur(v); setFilterClient(""); setFilterProjet(""); };
  const handleClientChange = (v) => { setFilterClient(v); setFilterProjet(""); };
  const reset = () => { setFilterRevendeur(""); setFilterClient(""); setFilterProjet(""); setSearch(""); };

  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleToggle = async (id) => {
    try {
      await evenementsAPI.toggle(id);
      fetchEvenements();
    } catch (err) {
      console.error("Erreur toggle:", err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await evenementsAPI.remove(id);
      setConfirmDelete(null);
      fetchEvenements();
    } catch (err) {
      console.error("Erreur suppression:", err);
    }
  };

  const total = evenements.length;
  const actifs = evenements.filter(e => e.statut === "Actif").length;
  const enPause = evenements.filter(e => e.statut === "En pause").length;
  const totalDeclenchements = evenements.reduce((s, e) => s + (e.declenchements || 0), 0);

  const selectStyle = "flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <>
      <Header title={t('events.title')} subtitle={t('events.subtitle')} />

      {/* Titre section */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
          <Activity size={16} className="text-blue-600" />
        </div>
        <h2 className="font-semibold text-slate-800">{t('events.title')}</h2>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-4">
        <div className="flex gap-3 mb-3">
          {adminMode === 'platform' && (
            <select value={filterRevendeur} onChange={e => handleRevendeurChange(e.target.value)} className={selectStyle}>
              <option value="">{t('common.allResellers')} ({revendeurs.length})</option>
              {revendeurs.map(r => <option key={r._id} value={r._id}>{r.nom}</option>)}
            </select>
          )}
          {!isMembre && (
            <select value={filterClient} onChange={e => handleClientChange(e.target.value)} className={selectStyle}>
              <option value="">{t('common.allClients')} ({clients.length})</option>
              {clients.map(c => <option key={c._id} value={c._id}>{c.nom}</option>)}
            </select>
          )}
          <select value={filterProjet} onChange={e => setFilterProjet(e.target.value)} className={selectStyle}>
            <option value="">{t('common.allProjects')} ({projets.length})</option>
            {projets.map(p => <option key={p._id} value={p._id}>{p.nom}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <SearchInput value={search} onChange={setSearch} placeholder="Rechercher..." />
          </div>
          {(filterRevendeur || filterClient || filterProjet || search) && (
            <button onClick={reset} className="flex items-center gap-1.5 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50">
              <RefreshCw size={14} /> {t('events.reset')}
            </button>
          )}
        </div>
      </div>

      {/* Stats + bouton */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Activity size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-400">{t('events.totalEvents')}</p>
              <p className="text-2xl font-bold text-slate-800">{total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle size={18} className="text-green-600" />
            </div>
            <div>
              <p className="text-xs text-slate-400">{t('events.active')}</p>
              <p className="text-2xl font-bold text-slate-800">{actifs}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Clock size={18} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-xs text-slate-400">{t('events.paused')}</p>
              <p className="text-2xl font-bold text-slate-800">{enPause}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Zap size={18} className="text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-slate-400">{t('events.triggers')}</p>
              <p className="text-2xl font-bold text-slate-800">{totalDeclenchements}</p>
            </div>
          </div>
        </div>
        {canDo('configIoT') && (
          <button
            disabled={!filtersSelected}
            onClick={() => {
              const projetObj = projets.find(p => p._id === filterProjet);
              navigate(`/evenements/creer?revendeur=${filterRevendeur}&client=${filterClient}&projet=${filterProjet}&projetNom=${encodeURIComponent(projetObj?.nom || '')}`);
            }}
            className={`rounded-2xl p-4 flex items-center justify-center gap-2 font-semibold text-sm transition-all ${
              filtersSelected
                ? "bg-blue-600 text-white hover:bg-blue-700 shadow-sm cursor-pointer"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            <Plus size={18} /> {t('events.addEvent')}
          </button>
        )}
      </div>

      {/* Liste événements */}
      {loading ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
          <RefreshCw size={32} className="text-gray-300 mx-auto mb-3 animate-spin" />
          <p className="text-slate-400">{t('events.loading')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {evenements.map(e => (
            <div key={e._id} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all cursor-pointer"
              onClick={() => navigate(`/evenements/creer?id=${e._id}&projet=${e.projet?._id || ""}&projetNom=${encodeURIComponent(e.projet?.nom || "")}`)}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Activity size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-semibold text-slate-800">{e.nom}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${typeBadge}`}>🏢 {e.revendeur?.nom}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${clientBadge}`}>👤 {e.client?.nom}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${projetBadge}`}>📁 {e.projet?.nom}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${statsBadge[e.statut]}`}>
                        <CheckCircle size={10} /> {e.statut}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 italic mb-3">"{e.description}"</p>
                    {/* Aperçu langage naturel */}
                    {(() => {
                      const preview = buildEvenementPreview(e);
                      if (!preview) return null;
                      return (
                        <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 mb-3">
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="text-[9px] bg-teal-600 text-white px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold">Langage naturel</span>
                          </div>
                          <p className="text-xs text-slate-700 leading-relaxed">
                            <span className="font-semibold text-teal-700">SI </span>
                            <span className="text-blue-600">{preview.conditionText}</span>
                          </p>
                          <p className="text-xs text-slate-700 leading-relaxed mt-0.5">
                            <span className="font-semibold text-orange-600">ALORS </span>
                            <span>{preview.actionText}</span>
                          </p>
                        </div>
                      );
                    })()}
                    <div className="flex items-center gap-5 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><RotateCcw size={12} /> {e.declenchements || 0} {t('events.triggersLabel')}</span>
                      <span className="flex items-center gap-1"><Bell size={12} /> {t('events.lastTrigger')} {e.dernier || "—"}</span>
                      <span className="flex items-center gap-1"><Activity size={12} /> {e.noeuds || 0} {t('events.nodesLabel')}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4 flex-shrink-0" onClick={ev => ev.stopPropagation()}>
                  <button
                    onClick={() => navigate(`/evenements/creer?id=${e._id}&projet=${e.projet?._id || ""}&projetNom=${encodeURIComponent(e.projet?.nom || "")}`)}
                    className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50"
                  >
                    <Eye size={14} /> {t('events.view')}
                  </button>
                  <button
                    onClick={() => handleToggle(e._id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium ${
                      e.statut === "Actif"
                        ? "bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100"
                        : "bg-green-50 text-green-700 border border-green-200 hover:bg-green-100"
                    }`}
                  >
                    {e.statut === "Actif" ? <><Pause size={14} /> {t('events.pause')}</> : <><Play size={14} /> {t('events.activate')}</>}
                  </button>
                  <button
                    onClick={() => setConfirmDelete(e._id)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm hover:bg-red-100"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {evenements.length === 0 && (
            <div className="bg-white rounded-2xl p-12 text-center border border-dashed border-gray-200">
              <Activity size={40} className="text-gray-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">{t('events.noEvents')}</p>
              <p className="text-slate-400 text-sm mt-1">
                {t('events.noEventsForProject')}
              </p>
            </div>
          )}
        </div>
      )}
      {/* Modal confirmation suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl p-6 shadow-xl max-w-sm w-full mx-4">
            <h3 className="font-semibold text-slate-800 mb-2">{t('events.deleteEvent')}</h3>
            <p className="text-sm text-slate-500 mb-5">{t('events.irreversible')}</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-slate-600 hover:bg-gray-50">{t('common.cancel')}</button>
              <button onClick={() => handleDelete(confirmDelete)} className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm hover:bg-red-700">{t('common.delete')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
